import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { DocumentDto } from '@app/generated/model';
import { Store } from '@ngrx/store';
import { State } from '@dwo/reducers';
import { getCurrentWorkOrder } from '@app/core/store/selectors/work-order.selector';
import { SafeStyle } from '@angular/platform-browser';
import { take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { FeatureFlagService } from '@app/core/services/feature-flag.service';
import {
  FeatureFlagKey,
  DocumentProcessStatusCode,
  DOCUMENT_PROCESS_STATUS_LABEL_KEY,
  DOCUMENT_PROCESS_STATUS_NOT_SET_LABEL_KEY,
} from '@dwo/enums';
import { MatDialog } from '@angular/material/dialog';
import { DocumentStatusDialogComponent } from './document-status-dialog/document-status-dialog.component';
import { WorkOrderDocumentService } from './work-order-document.service';

@Component({
  selector: 'app-work-order-document',
  templateUrl: './work-order-document.component.html',
  styleUrls: ['./work-order-document.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WorkOrderDocumentComponent implements OnInit {
  workOrderId: string;
  projectId: string;
  @Input() document: DocumentDto;
  @Input() hasNotification: boolean;
  thumbnailImage: SafeStyle = null;
  @Output() addToTabs = new EventEmitter<DocumentDto>();

  get enableRedlineMarkup(): boolean {
    return this.featureFlagService.isEnabled(FeatureFlagKey.EnableRedlineMarkup, this.projectId);
  }

  get showStatus(): boolean {
    return this.featureFlagService.isEnabled(FeatureFlagKey.EnableDocumentProcessStatus, this.projectId);
  }

  get liveStatusPending(): boolean {
    return this.documentService.isLiveStatusPending(this.document?.id);
  }

  get isStatusReadOnly(): boolean {
    return this.document?.documentProcessStatus?.id === DocumentProcessStatusCode.ForInfo;
  }

  readonly statusNotSetLabelKey = DOCUMENT_PROCESS_STATUS_NOT_SET_LABEL_KEY;

  // "no" (MIPS's user-facing code) is shown, not the internal doc_process_status_id; name is shown
  // as-is (raw MIPS value, untranslated) - only "Status:" is translated. Requires a resolved name -
  // a status without one (e.g. catalogue cache not warmed yet) falls back to the "not set" label
  // rather than rendering a broken "{{name}}" placeholder.
  get statusLabelParams(): { id: string; name: string } | null {
    const status = this.document?.documentProcessStatus;
    return status?.no != null && status?.name != null ? { id: status.no, name: status.name } : null;
  }

  readonly statusLabelKey = DOCUMENT_PROCESS_STATUS_LABEL_KEY;

  openStatusDialog(event: MouseEvent) {
    event.stopPropagation();
    // Status 5 (For Info) only ever comes from MIPS and cannot be changed in the app.
    if (this.isStatusReadOnly) {
      return;
    }

    this.documentService.getDocumentProcessStatuses().subscribe((statuses) => {
      this.dialog
        .open(DocumentStatusDialogComponent, {
          data: { currentStatusId: this.document.documentProcessStatus?.id ?? null, statuses },
        })
        .afterClosed()
        .subscribe((result) => {
          if (typeof result === 'number') {
            void this.documentService.updateDocumentProcessStatus(this.document.id, result);
          }
        });
    });
  }

  openInNewTab(event: MouseEvent) {
    this.addToTabs.emit(this.document);
  }

  openPreview(event: MouseEvent) {
    event.stopPropagation();
    this.router.navigate(['/work-order', this.workOrderId, 'document-preview', this.document.id]);
  }

  openMarkup(event: MouseEvent) {
    event.stopPropagation();
    this.router.navigate(['/work-order', this.workOrderId, 'pdf-markup', this.document.id]);
  }

  get is3dModel(): boolean {
    return (
      this.document?.documentType === 'MODEL_3D' ||
      ['FBX', 'GLB', 'GLTF'].includes(this.document?.fileType?.toUpperCase())
    );
  }

  get isVideoModel(): boolean {
    return (
      this.document?.documentType === 'VIDEO' ||
      ['MP4', 'MOV', 'WEBM', 'AVI'].includes(this.document?.fileType?.toUpperCase())
    );
  }

  get isTimeDrawing(): boolean {
    return this.document?.documentType === 'LINK';
  }

  constructor(
    private store: Store<State>,
    private router: Router,
    private featureFlagService: FeatureFlagService,
    private dialog: MatDialog,
    private documentService: WorkOrderDocumentService,
  ) {}

  async ngOnInit() {
    this.store
      .select(getCurrentWorkOrder)
      .pipe(take(1))
      .subscribe((wo) => {
        this.workOrderId = wo?.id;
        this.projectId = wo?.projectId;
      });
  }
}
